import { useMutation } from '@apollo/client';
import { PATCH_LAST_RECURRING_PAYMENT } from '@mutations/patchLastRecurringPayment';
import { showToast } from 'components/common/CustomToast';
import usePollSalesOrderLastRecurringPaymentStatus from 'hooks/usePollSalesOrderLastRecurringPaymentStatus';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mutation,
  MutationPatchLastRecurringPaymentArgs,
} from 'types/generatedSchemaTypes';

type UseRetryRecurringPaymentProps = {
  salesOrderUid: string | undefined;
};

const useRetryRecurringPayment = ({
  salesOrderUid,
}: UseRetryRecurringPaymentProps) => {
  const { t } = useTranslation();
  const [isAwaitingRetryResult, setIsAwaitingRetryResult] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [patchLastRecurringPayment, { loading: patchLoading }] = useMutation<
    Mutation,
    MutationPatchLastRecurringPaymentArgs
  >(PATCH_LAST_RECURRING_PAYMENT, {
    onCompleted: () => {
      setIsAwaitingRetryResult(true);
    },
    onError: () => {
      setIsProcessing(false);
      showToast(t('error.updatePermit'), 'error');
    },
  });

  const { lastPollingCompleted, isFailed } =
    usePollSalesOrderLastRecurringPaymentStatus(
      {
        salesOrderUid: salesOrderUid ?? '',
      },
      false
    );

  useEffect(() => {
    if (!lastPollingCompleted || !isAwaitingRetryResult) return;

    setIsAwaitingRetryResult(false);
    setIsProcessing(false);

    if (isFailed === true) {
      showToast(t('permit.permitRecurringPaymentFailed'), 'error');
      return;
    }
    showToast(t('permit.permitRecurringPaymentSuccess'), 'success');
  }, [lastPollingCompleted, isAwaitingRetryResult, isFailed, t]);

  const retryPayment = async (cardId: string | undefined) => {
    if (!salesOrderUid) {
      showToast(t('error.updatePermit'), 'error');
      return;
    }

    setIsProcessing(true);
    return patchLastRecurringPayment({
      variables: {
        input: {
          salesOrderUid,
          cardId,
        },
      },
    });
  };

  return {
    retryPayment,
    isProcessing,
    isFailed,
    patchLoading,
  };
};

export default useRetryRecurringPayment;
